import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  Table,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Tbody,
  useToast,
  VStack,
  Code,
  Spinner,
  Center,
  Divider,
} from '@chakra-ui/react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { Match } from '../../types';
import { MUNDIAL_LEAGUE, MundialTeam } from '../../types/mundial';
import { mundialTeamService } from '../../services/mundial/mundialTeamService';
import { mundialMatchService } from '../../services/mundial/mundialMatchService';
import { ResponsiveTableContainer } from '../ui/ResponsiveTableContainer';
import { KNOCKOUT_ROUNDS } from '../../constants/mundialBracket';

const PHASE_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'Todos los partidos' },
  { value: 'group', label: 'Fase de grupos' },
  ...KNOCKOUT_ROUNDS.map((r) => ({ value: r.matchType, label: r.phaseLabel })),
];

const PHASE_LABEL: Record<string, string> = {
  group: 'Grupos',
  ...Object.fromEntries(KNOCKOUT_ROUNDS.map((r) => [r.matchType, r.phaseLabel])),
};

function formatMatchDate(m: Match): string {
  const d = m.date instanceof Date ? m.date : new Date(m.date);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('es-MX', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function sortMatches(a: Match, b: Match): number {
  const ta = new Date(a.date).getTime();
  const tb = new Date(b.date).getTime();
  if (ta !== tb) return ta - tb;
  return (a.bracket_slot ?? a.group ?? '').localeCompare(b.bracket_slot ?? b.group ?? '');
}

export const AssignMatchTeams: React.FC = () => {
  const [matches, setMatches] = useState<Match[]>([]);
  const [teams, setTeams] = useState<MundialTeam[]>([]);
  const [loading, setLoading] = useState(true);
  const [phaseFilter, setPhaseFilter] = useState('round_of_32');
  const [search, setSearch] = useState('');
  const [matchIdInput, setMatchIdInput] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [codeA, setCodeA] = useState('');
  const [codeB, setCodeB] = useState('');
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [teamsData, matchesData] = await Promise.all([
        mundialTeamService.getAllTeams(),
        mundialMatchService.getMundialMatches(),
      ]);
      setTeams(teamsData);
      setMatches(matchesData.sort(sortMatches));
    } catch (e) {
      toast({
        title: 'No se pudieron cargar los partidos',
        description: e instanceof Error ? e.message : 'Error desconocido',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load().catch(console.error);
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return matches.filter((m) => {
      if (phaseFilter !== 'all' && m.match_type !== phaseFilter) return false;
      if (!q) return true;
      const haystack = [
        m.id,
        m.bracket_slot,
        m.group,
        m.team_a,
        m.team_b,
        m.team_a_code,
        m.team_b_code,
        m.phase,
        m.match_type,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [matches, phaseFilter, search]);

  const selected = matches.find((m) => m.id === selectedId);

  const selectMatch = (m: Match) => {
    setSelectedId(m.id);
    setMatchIdInput(m.id);
    setCodeA(m.team_a_code?.toUpperCase() ?? '');
    setCodeB(m.team_b_code?.toUpperCase() ?? '');
  };

  const loadByMatchId = async () => {
    const id = matchIdInput.trim();
    if (!id) return;

    const cached = matches.find((m) => m.id === id);
    if (cached) {
      selectMatch(cached);
      return;
    }

    setLoading(true);
    try {
      const snap = await getDoc(doc(db, 'matches', id));
      if (!snap.exists()) {
        toast({ title: 'No existe ese match_id', status: 'warning' });
        return;
      }
      const data = snap.data();
      if (data.league !== MUNDIAL_LEAGUE) {
        toast({ title: 'Ese partido no es del Mundial FIFA', status: 'warning' });
        return;
      }
      const m = {
        id: snap.id,
        ...data,
        date: data.date?.toDate?.() ?? data.date,
        created_at: data.created_at?.toDate?.() ?? data.created_at,
        updated_at: data.updated_at?.toDate?.() ?? data.updated_at,
      } as Match;
      setMatches((prev) => {
        if (prev.some((x) => x.id === m.id)) return prev;
        return [...prev, m].sort(sortMatches);
      });
      selectMatch(m);
    } catch (e) {
      toast({
        title: 'Error al cargar match_id',
        description: e instanceof Error ? e.message : 'Error desconocido',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const saveTeams = async () => {
    if (!selectedId || !codeA || !codeB) {
      toast({ title: 'Selecciona partido y ambos equipos', status: 'warning' });
      return;
    }
    const infoA = teams.find((t) => t.code.toUpperCase() === codeA.toUpperCase());
    const infoB = teams.find((t) => t.code.toUpperCase() === codeB.toUpperCase());
    if (!infoA || !infoB) {
      toast({ title: 'Código de equipo no encontrado en mundial_teams', status: 'error' });
      return;
    }

    setSaving(true);
    try {
      await updateDoc(doc(db, 'matches', selectedId), {
        team_a: infoA.name,
        team_b: infoB.name,
        team_a_code: infoA.code.toUpperCase(),
        team_b_code: infoB.code.toUpperCase(),
        team_a_logo: infoA.flag_url ?? null,
        team_b_logo: infoB.flag_url ?? null,
        updated_at: new Date(),
      });
      toast({
        title: 'Equipos guardados',
        description: `${selectedId}: ${infoA.code} vs ${infoB.code}`,
        status: 'success',
        duration: 6000,
      });
      await load();
      setSelectedId(selectedId);
      setCodeA(infoA.code.toUpperCase());
      setCodeB(infoB.code.toUpperCase());
    } catch (e) {
      toast({
        title: 'No se pudo guardar',
        description: e instanceof Error ? e.message : 'Error desconocido',
        status: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading && matches.length === 0) {
    return (
      <Center py={8}>
        <Spinner />
      </Center>
    );
  }

  return (
    <VStack align="stretch" spacing={5}>
      <Text fontSize="sm" color="gray.600">
        Asigna equipos directamente al documento <Code>matches</Code> por <Code>match_id</Code>.
        Los pronósticos no se mueven: siguen ligados al mismo <Code>match_id</Code>.
      </Text>

      <Box p={4} borderWidth="1px" borderRadius="md" bg="gray.50">
        <VStack align="stretch" spacing={3}>
          <Text fontWeight="semibold" fontSize="sm">
            Buscar por match_id
          </Text>
          <HStack align="flex-end" flexWrap="wrap">
            <FormControl flex="1" minW="240px">
              <FormLabel fontSize="sm">match_id (Firestore)</FormLabel>
              <InputGroup>
                <Input
                  fontFamily="mono"
                  fontSize="sm"
                  placeholder="Pega el ID del documento"
                  value={matchIdInput}
                  onChange={(e) => setMatchIdInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && loadByMatchId()}
                />
                <InputRightElement width="4.5rem">
                  <Button h="1.75rem" size="sm" onClick={loadByMatchId}>
                    Ir
                  </Button>
                </InputRightElement>
              </InputGroup>
            </FormControl>
          </HStack>
        </VStack>
      </Box>

      <HStack flexWrap="wrap" spacing={3}>
        <FormControl maxW="xs">
          <FormLabel fontSize="sm">Fase</FormLabel>
          <Select
            size="sm"
            value={phaseFilter}
            onChange={(e) => setPhaseFilter(e.target.value)}
          >
            {PHASE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </FormControl>
        <FormControl flex="1" minW="200px">
          <FormLabel fontSize="sm">Buscar</FormLabel>
          <Input
            size="sm"
            placeholder="Slot, grupo, equipo, código…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </FormControl>
        <Button size="sm" variant="outline" alignSelf="flex-end" onClick={() => load()}>
          Recargar
        </Button>
      </HStack>

      <ResponsiveTableContainer>
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>Fecha</Th>
              <Th>Fase</Th>
              <Th>Slot / Grupo</Th>
              <Th>match_id</Th>
              <Th>Equipos actuales</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filtered.map((m) => (
              <Tr
                key={m.id}
                bg={m.id === selectedId ? 'blue.50' : undefined}
                cursor="pointer"
                _hover={{ bg: m.id === selectedId ? 'blue.50' : 'gray.50' }}
                onClick={() => selectMatch(m)}
              >
                <Td whiteSpace="nowrap">{formatMatchDate(m)}</Td>
                <Td>
                  <Badge fontSize="xs">
                    {PHASE_LABEL[m.match_type ?? ''] ?? m.phase ?? m.match_type}
                  </Badge>
                </Td>
                <Td fontWeight="medium">{m.bracket_slot ?? m.group ?? '—'}</Td>
                <Td maxW="180px">
                  <Code fontSize="xs" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                    {m.id}
                  </Code>
                </Td>
                <Td>
                  {m.team_a_code ?? '?'} vs {m.team_b_code ?? '?'}
                  <Text fontSize="xs" color="gray.500" noOfLines={1}>
                    {m.team_a} vs {m.team_b}
                  </Text>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </ResponsiveTableContainer>

      {filtered.length === 0 && (
        <Text fontSize="sm" color="gray.500" textAlign="center">
          No hay partidos con ese filtro.
        </Text>
      )}

      {selected && (
        <>
          <Divider />
          <Box p={4} borderWidth="2px" borderColor="blue.200" borderRadius="md">
            <VStack align="stretch" spacing={4}>
              <Box>
                <Text fontWeight="bold" mb={1}>
                  Editar equipos
                </Text>
                <Text fontSize="sm" color="gray.600">
                  match_id: <Code>{selected.id}</Code>
                  {selected.bracket_slot && (
                    <>
                      {' '}
                      · slot <Code>{selected.bracket_slot}</Code>
                    </>
                  )}
                  {selected.group && (
                    <>
                      {' '}
                      · grupo <Code>{selected.group}</Code>
                    </>
                  )}
                </Text>
                {(selected.source_slot_a || selected.source_slot_b) && (
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    source_slot: {selected.source_slot_a ?? '—'} | {selected.source_slot_b ?? '—'}
                  </Text>
                )}
              </Box>

              <HStack spacing={4} flexWrap="wrap" align="flex-start">
                <FormControl flex="1" minW="200px">
                  <FormLabel>Local (team_a)</FormLabel>
                  <Select value={codeA} onChange={(e) => setCodeA(e.target.value)}>
                    <option value="">— Seleccionar —</option>
                    {teams.map((t) => (
                      <option key={t.code} value={t.code}>
                        {t.code} — {t.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl flex="1" minW="200px">
                  <FormLabel>Visitante (team_b)</FormLabel>
                  <Select value={codeB} onChange={(e) => setCodeB(e.target.value)}>
                    <option value="">— Seleccionar —</option>
                    {teams.map((t) => (
                      <option key={t.code} value={t.code}>
                        {t.code} — {t.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </HStack>

              <Button
                colorScheme="blue"
                alignSelf="flex-start"
                isLoading={saving}
                onClick={saveTeams}
              >
                Guardar equipos en este match_id
              </Button>
            </VStack>
          </Box>
        </>
      )}
    </VStack>
  );
};
